import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactSection from "../components/ContactSection";
import "./InfoPage.css";
import { useState } from "react";

const pageContent = {
  about: {
    eyebrow: "ABOUT CLOUD CALL",
    title: "Every conversation should move your business forward.",
    intro: "Cloud Call gives teams an intelligent voice partner that is available around the clock, understands customer intent, and knows when a human should take over.",
    items: [
      ["Human when it matters", "Give your team the context they need before they pick up, while keeping every interaction personal."],
      ["Built for momentum", "Turn routine calls into quick resolutions and let your people spend more time on meaningful conversations."],
      ["Connected by design", "Bring voice, customer data, and follow-up workflows together in one clear operating rhythm."],
    ],
    detailTitle: "A better standard for customer conversations",
    detail: "Cloud Call is designed around a simple idea: technology should make conversations feel more human, not less. We help teams remove friction from the moments that matter, while giving leaders a clearer view of what customers need.",
  },
  features: {
    eyebrow: "THE CLOUD CALL PLATFORM",
    title: "A calmer, smarter way to run every call.",
    intro: "From the first hello to the final handover, Cloud Call helps your team respond faster, work with better context, and learn from every conversation.",
    items: [
      ["AI-powered conversations", "Handle common questions naturally with an always-on voice agent that sounds and responds like a thoughtful teammate."],
      ["Smart routing", "Direct every caller to the right person, department, or next step without putting them through a maze."],
      ["CRM-ready insights", "Capture call notes, outcomes, and customer intent automatically so your records stay useful and current."],
      ["Human handover", "Move complex conversations to an agent with the full story intact, so customers never have to start over."],
    ],
    detailTitle: "One platform, from first ring to resolution",
    detail: "Cloud Call combines conversation handling, intelligent routing, and useful call insights in one workflow. That means your team can move quickly without losing the context that makes service feel personal.",
  },
  industries: {
    eyebrow: "INDUSTRY USE CASES",
    title: "Voice automation that understands your world.",
    intro: "Every industry has its own pace, language, and customer expectations. Cloud Call adapts the conversation to the work your team does every day.",
    items: [
      ["Financial services", "Qualify enquiries, provide account support, and route sensitive conversations to the right specialist."],
      ["Healthcare", "Make appointments and reminders easier while giving staff more time for the people in front of them."],
      ["Real estate", "Respond to new leads immediately, schedule viewings, and keep prospect conversations moving."],
      ["Retail and services", "Answer availability questions, manage bookings, and protect your customer experience during busy periods."],
    ],
    detailTitle: "Flexible enough for the way you work",
    detail: "Start with the conversations that create the most pressure for your team, then expand as you learn. Cloud Call supports practical improvements without asking you to rebuild your entire operation.",
  },
};

const faqs = [
  ["What is Cloud Call?", "Cloud Call is an AI-powered voice solution that helps businesses answer calls, manage routine enquiries, route conversations, and support human agents with better context."],
  ["Can callers speak to a real person?", "Yes. Cloud Call can hand a conversation to your team whenever a request needs human judgement, specialist knowledge, or a personal touch."],
  ["Does Cloud Call work with our existing systems?", "Cloud Call is designed to fit into your current workflow. During a demo, we can discuss your CRM, call process, and the integrations your team needs."],
  ["What kind of businesses can use it?", "Cloud Call can support customer-facing teams in financial services, healthcare, real estate, retail, and other service-led industries."],
  ["How quickly can we get started?", "We begin by understanding your call volumes, common customer questions, and goals. From there, we recommend a focused starting point and a practical rollout plan."],
  ["How do I request a demo?", "Use the Request a Demo page to send us your details. Our team will follow up to understand your needs and arrange a useful walkthrough."],
];

export default function InfoPage({ page }) {
  const [openFaq, setOpenFaq] = useState(0);

  if (page === "faq") {
    return (
      <>
        <Navbar />
        <main className="info-page faq-page">
          <section className="info-hero">
            <p className="info-eyebrow">FREQUENTLY ASKED QUESTIONS</p>
            <h1>Clear answers for your next conversation.</h1>
            <p>Everything you need to know about how Cloud Call helps teams handle calls with more speed, context, and care.</p>
          </section>
          <section className="faq-list" aria-label="Frequently asked questions">
            {faqs.map(([question, answer], index) => (
              <article className={`faq-item ${openFaq === index ? "is-open" : ""}`} key={question}>
                <button type="button" onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}>
                  <span>{question}</span>
                  <span className="faq-icon">{openFaq === index ? "-" : "+"}</span>
                </button>
                {openFaq === index && <p>{answer}</p>}
              </article>
            ))}
          </section>
          <section className="info-cta">
            <p className="info-eyebrow">STILL CURIOUS?</p>
            <h2>Let us answer your specific questions.</h2>
            <a href="/contact" className="info-button">CONTACT US</a>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  if (page === "contact" || page === "demo") {
    return (
      <>
        <Navbar />
        <main className="info-page info-page--contact">
          <div className="info-hero">
            <p className="info-eyebrow">{page === "demo" ? "SEE CLOUD CALL IN ACTION" : "LET'S TALK"}</p>
            <h1>{page === "demo" ? "Bring a better call experience to your team." : "Questions, ideas, or a call worth improving?"}</h1>
            <p>{page === "demo" ? "Tell us about your team and we will show you how Cloud Call can fit into the way you already work." : "Our team is ready to help you find the right next step for your customers and your people."}</p>
          </div>
          <ContactSection />
        </main>
        <Footer />
      </>
    );
  }

  const content = pageContent[page] || pageContent.about;

  return (
    <>
      <Navbar />
      <main className="info-page">
        <section className="info-hero">
          <p className="info-eyebrow">{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
        </section>
        <section className="info-grid" aria-label={`${page} highlights`}>
          {content.items.map(([title, description], index) => (
            <article className="info-card" key={title}>
              <span className="info-card-number">0{index + 1}</span>
              <h2>{title}</h2>
              <p>{description}</p>
            </article>
          ))}
        </section>
        <section className="info-detail">
          <div>
            <p className="info-eyebrow">WHY IT MATTERS</p>
            <h2>{content.detailTitle}</h2>
          </div>
          <p>{content.detail}</p>
        </section>
        <section className="info-cta">
          <p className="info-eyebrow">READY WHEN YOU ARE</p>
          <h2>Make your next call count.</h2>
          <a href="/demo" className="info-button">REQUEST A DEMO</a>
        </section>
      </main>
      <Footer />
    </>
  );
}